import { DatePicker, Form, Select } from "antd";
import { ReactElement, useEffect, useState } from "react";

import Label from "../../components/label/label.component";
import CustomInput from "../../components/custom-input/custom-input.component";
import "./edit-account-details.scss";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AxiosResponse } from "axios";
import instance from "../../store/axios";
import {
  ADD_TO_BREADCRUMB,
  LOADING,
  REMOVE_FROM_BREADCRUMB,
} from "../../store/actions";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import ReactCountryFlag from "react-country-flag";
import CollapseComponent from "../../components/collapse.component";
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import CustomModal from "src/components/custom-modal/custom-modal";
import DeleteModal from "src/components/delete-modal/delete-modal";
import { getStandingDataOptions } from "src/utils/helper/functions";

const { Option } = Select;

const EditAccountDetails: React.FC = (): ReactElement => {
  const { accountId, activeKyc } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isApproveCase = queryParams.get("approve");
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const [submitForm, setSubmitForm] = useState(false);
  const [accountDetails, setAccountDetails] = useState<any>();
  const [standingData, setStandingData] = useState<any>();
  const [countriesData, setCountriesData] = useState([]);
  const [statesData, setStatesData] = useState([]);
  const [openUpdateFatca, setOpenUpdateFatca] = useState<boolean>(false);

  useEffect(() => {
    async function prepareData() {
      dispatch({ type: LOADING, payload: true });

      await getStandindData();
      await getCountriesData();
      await getKycApi();
      dispatch({ type: LOADING, payload: false });
    }

    prepareData();

    dispatch({
      type: ADD_TO_BREADCRUMB,
      payload: {
        breadcrumb: [
          {
            text: `Update personal details`,
            type: "text",
          },
        ],
      },
    });

    return () => {
      dispatch({
        type: REMOVE_FROM_BREADCRUMB,
        payload: {
          breadcrumb: [
            {
              text: `Update personal details`,
            },
          ],
        },
      });
    };
  }, []);

  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmitForm(true);
      },
      () => {
        setSubmitForm(false);
      }
    );
  }, [values]);

  const getKycApi = async (): Promise<void> => {
    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}?kycId=${activeKyc}`
      );
      setAccountDetails(response.data.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/account-details/${accountId}/${activeKyc}/edit`
      );
    }
  };

  const getStandindData = async (): Promise<void> => {
    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/option/standing-data`
      );
      setStandingData(response.data.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/account-details/${accountId}/${activeKyc}/edit`
      );
    }
  };

  const getCountriesData = async (): Promise<void> => {
    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION
        }/option/countries?partial=${1}`
      );
      setCountriesData(response.data.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/account-details/${accountId}/${activeKyc}/edit`
      );
    }
  };

  const onCountryChange = (value: any) => {
    if (value) {
      getStatesData(value);
    }
  };

  const getStatesData = async (value: any): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/option/${value}/states`
      );
      setStatesData(response.data.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/account-details/${accountId}/${activeKyc}/edit`
      );
    }

    dispatch({ type: LOADING, payload: false });
  };

  const onSubmit = async (values: any): Promise<void> => {
    dispatch({ type: LOADING, payload: true });

    values.kycId = accountDetails.accountInfo[0]?.id;
    values.branch = "KW";

    if (!values.otherNationalityId) {
      values.otherNationalityId = null;
    }

    try {
      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}/personal/update`,
        values
      );
      toast.success("Personal data updated successfully");
      if (values.nationalityId === 187 || values.otherNationalityId === 187) {
        setOpenUpdateFatca(true);
      } else {
        navigate(
          `/home/business-account/${accountId}/${activeKyc}/details?tab=1${isApproveCase ? "&approve=1" : ""
          }`
        );
      }
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/account-details/${accountId}/${activeKyc}/edit${isApproveCase ? "?approve=1" : ""
        }`
      );
    }

    dispatch({ type: LOADING, payload: false });
  };

  return (
    <div className="edit-account-details-container flex flex-col">
      {/* <h3 className=" font-semibold text-xl">Personal details</h3> */}
      <BreadcrumbComponent />
      {accountDetails && (
        <Form
          className="add-edit-form"
          onFinish={onSubmit}
          initialValues={{
            civilIdNumber: accountDetails?.accountInfo[0]?.civilIdNumber,
            civilIdExpiry: accountDetails?.accountInfo[0]?.civilIdNumber
              ? dayjs(accountDetails?.accountInfo[0]?.civilIdExpiry)
              : null,
            passportNumber: accountDetails?.accountInfo[0]?.passportNumber,
            passportExpiry: accountDetails?.accountInfo[0]?.passportNumber
              ? dayjs(accountDetails?.accountInfo[0]?.passportExpiry)
              : null,
            nationalityId: accountDetails?.accountInfo[0]?.nationalityId,
            otherNationalityId:
              accountDetails?.accountInfo[0]?.otherNationalityId,
            birthDate: accountDetails?.accountInfo[0]?.birthDate
              ? dayjs(accountDetails?.accountInfo[0]?.birthDate)
              : null,
            titleId: accountDetails?.accountInfo[0]?.titleId,
            nameEn: accountDetails?.accountInfo[0]?.nameEn,
            nameAr: accountDetails?.accountInfo[0]?.nameAr,
            birthPlaceId: accountDetails?.accountInfo[0]?.birthPlaceId,
            countryId: accountDetails?.accountInfo[0]?.countryId,
            birthPlaceCity: accountDetails?.accountInfo[0]?.birthPlaceCity,
            genderId: accountDetails?.accountInfo[0]?.genderId,
            guardianTitleId: accountDetails?.accountInfo[0].guardianTitle?.id,
            guardianNameEn: accountDetails?.accountInfo[0].guardianNameEn,
            guardianResidentCountryId:
              accountDetails?.accountInfo[0].guardianResidentCountry?.id,
            numberDependents: accountDetails?.accountInfo[0]?.numberDependents,
            maritalStatusId: accountDetails?.accountInfo[0]?.maritalStatusId,
            idTypeId: accountDetails?.accountInfo[0]?.idTypeId,
          }}
          form={form}
        >
          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-lg font-semibold">
                  {accountDetails?.clientSubType?.description === "Minor"
                    ? "Minor details"
                    : "Personal details"}
                </h3>
              </div>
            }
          >
            <div className="fields flex justify-space-between wrap">
              <div className="field flex column">
                <Label text="Title" />
                <Form.Item
                  name="titleId"
                  rules={[
                    {
                      required: true,
                      message: "Please select title",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select title"
                    options={[
                      ...getStandingDataOptions(
                        standingData,
                        "title",
                        ["kw", "gc"],
                        false
                      ),
                    ]}
                  />
                </Form.Item>
              </div>

              <div className="field flex column">
                <Label text="Full Name in English" />
                <Form.Item
                  name="nameEn"
                  rules={[
                    {
                      required: true,
                      message: "Please input full name in English!",
                    },
                  ]}
                >
                  <CustomInput placeholder="Enter full name in English" />
                </Form.Item>
              </div>

              <div className="field flex column">
                <Label text="Full Name in Arabic" />
                <Form.Item
                  name="nameAr"
                  rules={[
                    {
                      required: true,
                      message: "Please input full name in arabic!",
                    },
                  ]}
                >
                  <CustomInput placeholder="Enter full name in arabic" />
                </Form.Item>
              </div>

              <div className="field flex column">
                <Label text="Country" />
                <Form.Item
                  name="countryId"
                  rules={[
                    {
                      required: true,
                      message: "Please select the Country!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Country"
                    allowClear
                    showSearch
                    filterOption={(input, option: any) => {
                      return (option?.key.toLowerCase() ?? "").includes(
                        input.toLowerCase()
                      );
                    }}
                    onChange={onCountryChange}
                  >
                    {countriesData.map((country: any) => (
                      <Option value={country.id} key={country.nameEn}>
                        <div
                          className="flex align-center"
                          style={{ gap: "5px" }}
                        >
                          <ReactCountryFlag
                            style={{
                              fontSize: "24px",
                              marginInlineEnd: "8px",
                            }}
                            countryCode={country?.countryCode}
                            svg
                          />
                          <span>{country.nameEn}</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className="field flex column">
                <Label text="Birth place - Country" />
                <Form.Item
                  name="birthPlaceId"
                  rules={[
                    {
                      required: true,
                      message: "Please select the birth place - Country!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select birth place - Country"
                    allowClear
                    showSearch
                    filterOption={(input, option: any) => {
                      return (option?.key.toLowerCase() ?? "").includes(
                        input.toLowerCase()
                      );
                    }}
                    onChange={onCountryChange}
                  >
                    {countriesData.map((country: any) => (
                      <Option value={country.id} key={country.nameEn}>
                        <div
                          className="flex align-center"
                          style={{ gap: "5px" }}
                        >
                          <ReactCountryFlag
                            style={{
                              fontSize: "24px",
                              marginInlineEnd: "8px",
                            }}
                            countryCode={country?.countryCode}
                            svg
                          />
                          <span>{country.nameEn}</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className="field flex flex-col">
                <Label text="Birth place - City" />
                <Form.Item
                  name="birthPlaceCity"
                  rules={[
                    {
                      required: true,
                      message: "Please input birth place - City!",
                    },
                  ]}
                >
                  <CustomInput placeholder="Enter birth place - City" />
                </Form.Item>
              </div>

              <div className="field flex flex-col">
                <Label text="Gender" />
                <Form.Item
                  name="genderId"
                  rules={[
                    {
                      required: true,
                      message: "Please select gender",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select gender"

                    options={[
                      ...getStandingDataOptions(
                        standingData,
                        "gender",
                        ["kw", "gc"],
                        false
                      ),
                    ]}
                  />
                </Form.Item>
              </div>

              <div className="field flex flex-col">
                <Label
                  text={
                    accountDetails?.branch === "SA"
                      ? "National ID"
                      : "Civil ID Number"
                  }
                />
                <Form.Item
                  name="civilIdNumber"
                  rules={[
                    {
                      required: form.getFieldValue("countryId") === 98,
                      message: "Please input civil ID number!",
                    },
                  ]}
                >
                  <CustomInput placeholder="Enter Civil ID Number" />
                </Form.Item>
              </div>

              <div className="field flex flex-col">
                <Label
                  text={
                    accountDetails?.branch === "SA"
                      ? "National ID expiry date"
                      : "Civil ID expiry date"
                  }
                />
                <Form.Item
                  name="civilIdExpiry"
                  rules={[
                    {
                      required: form.getFieldValue("countryId") === 98,
                      message: "Please input expiry date!",
                    },
                  ]}
                >
                  <DatePicker
                    format={"YYYY-MM-DD"}
                    placeholder="Select civil ID expiry date"
                    onChange={(e: any) => e?.format("YYYY-MM-DD")}
                    showNow={false}
                  />
                </Form.Item>
              </div>

              <div className="field flex flex-col">
                <Label text="Passport number" />
                <Form.Item name="passportNumber">
                  <CustomInput placeholder="Enter passport number" />
                </Form.Item>
              </div>

              <div className="field flex flex-col">
                <Label text="Passport expiry date" />
                <Form.Item name="passportExpiry">
                  <DatePicker
                    format={"YYYY-MM-DD"}
                    placeholder="Select expiry date"
                    onChange={(e: any) => e?.format("YYYY-MM-DD")}
                  />
                </Form.Item>
              </div>

              <div className="field flex column">
                <Label text="Nationality" />
                <Form.Item
                  name="nationalityId"
                  rules={[
                    {
                      required: true,
                      message: "Please select the nationality!",
                    },
                  ]}
                >
                  <Select
                    placeholder="Select Nationality"
                    allowClear
                    showSearch
                    filterOption={(input, option: any) => {
                      return (option?.key.toLowerCase() ?? "").includes(
                        input.toLowerCase()
                      );
                    }}
                    onChange={onCountryChange}
                  >
                    {countriesData.map((country: any) => (
                      <Option value={country.id} key={country.nameEn}>
                        <div
                          className="flex align-center"
                          style={{ gap: "5px" }}
                        >
                          <ReactCountryFlag
                            style={{
                              fontSize: "24px",
                              marginInlineEnd: "8px",
                            }}
                            countryCode={country?.countryCode}
                            svg
                          />
                          <span>{country.nameEn}</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className="field flex column">
                <Label text="Other nationality" />
                <Form.Item name="otherNationalityId">
                  <Select
                    placeholder="Select Other Nationality"
                    allowClear
                    showSearch
                    filterOption={(input, option: any) => {
                      return (option?.key.toLowerCase() ?? "").includes(
                        input.toLowerCase()
                      );
                    }}
                  >
                    {countriesData.map((country: any) => (
                      <Option value={country.id} key={country.nameEn}>
                        <div
                          className="flex align-center"
                          style={{ gap: "5px" }}
                        >
                          <ReactCountryFlag
                            style={{
                              fontSize: "24px",
                              marginInlineEnd: "8px",
                            }}
                            countryCode={country?.countryCode}
                            svg
                          />
                          <span>{country.nameEn}</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              <div className="field flex column">
                <Label text="Date of Birth" />
                <Form.Item
                  name="birthDate"
                  rules={[
                    {
                      required: true,
                      message: "Please input date of birth",
                    },
                  ]}
                >
                  <DatePicker
                    format={"YYYY-MM-DD"}
                    placeholder="Select date of birth"
                    onChange={(e: any) => e?.format("YYYY-MM-DD")}
                  />
                </Form.Item>
              </div>

              {accountDetails?.branch === "SA" && (
                <div className="field flex flex-col">
                  <Label text={" Number of dependants"} />
                  <Form.Item
                    name="numberDependents"
                    rules={[
                      {
                        required: true,
                        message: "Please input the number of dependants!",
                      },
                    ]}
                  >
                    <CustomInput placeholder="Enter number of dependants" />
                  </Form.Item>
                </div>
              )}
              {accountDetails?.branch === "SA" && (
                <div className="field flex flex-col">
                  <Label text="Marital status" />
                  <Form.Item
                    name="maritalStatusId"
                    rules={[
                      {
                        required: true,
                        message: "Please select marital status",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select marital status"

                      options={[
                        ...getStandingDataOptions(
                          standingData,
                          "maritalStatus_sa",
                          ["kw", "gc"],
                          false
                        ),
                      ]}
                    />
                  </Form.Item>
                </div>
              )}
              {accountDetails?.branch === "SA" && (
                <div className="field flex flex-col">
                  <Label text="ID type" />
                  <Form.Item
                    name="idTypeId"
                    rules={[
                      {
                        required: true,
                        message: "Please select ID type",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select ID type"

                      options={[
                        ...getStandingDataOptions(
                          standingData,
                          "iDType",
                          ["kw", "gc"],
                          false
                        ),
                      ]}
                    />
                  </Form.Item>
                </div>
              )}
            </div>
          </CollapseComponent>
          {accountDetails?.clientSubType?.description === "Minor" && (
            <CollapseComponent
              header={
                <div className="flex gap-3 items-center">
                  <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                  <h3 className=" text-lg font-semibold">Guardian details</h3>
                </div>
              }
            >
              <div className="fields flex justify-space-between wrap">
                <div className="field flex column">
                  <Label text="Title" />
                  <Form.Item
                    name="guardianTitleId"
                    rules={[
                      {
                        required: true,
                        message: "Please select title",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select title"

                      options={[
                        ...getStandingDataOptions(
                          standingData,
                          "title",
                          ["kw", "gc"],
                          false
                        ),
                      ]}
                    />
                  </Form.Item>
                </div>

                <div className="field flex column">
                  <Label text="Full Name in English" />
                  <Form.Item
                    name="guardianNameEn"
                    rules={[
                      {
                        required: true,
                        message: "Please input full name in English!",
                      },
                    ]}
                  >
                    <CustomInput placeholder="Enter full name in English" />
                  </Form.Item>
                </div>

                <div className="field flex column">
                  <Label text="Country" />
                  <Form.Item
                    name="guardianResidentCountryId"
                    rules={[
                      {
                        required: true,
                        message: "Please select the Country!",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select Country"
                      allowClear
                      showSearch
                      filterOption={(input, option: any) => {
                        return (option?.key.toLowerCase() ?? "").includes(
                          input.toLowerCase()
                        );
                      }}
                    >
                      {countriesData.map((country: any) => (
                        <Option value={country.id} key={country.nameEn}>
                          <div
                            className="flex align-center"
                            style={{ gap: "5px" }}
                          >
                            <ReactCountryFlag
                              style={{
                                fontSize: "24px",
                                marginInlineEnd: "8px",
                              }}
                              countryCode={country?.countryCode}
                              svg
                            />
                            <span>{country.nameEn}</span>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </div>
            </CollapseComponent>
          )}
          <div className="flex w-full flex-wrap items-center justify-end mt-7  pt-5">
            <div className="flex justify-between items-center w-[70%] md:w-[48%]">
              <button
                onClick={() =>
                  navigate(
                    `/home/business-account/${accountId}/${activeKyc}/details?tab=1${isApproveCase ? "&approve=1" : ""
                    }`
                  )
                }
                className="rounded-lg bg-white w-[48%] py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
              >
                Cancel
              </button>
              <button
                disabled={!submitForm}
                className="disabled:opacity-50 disabled:cursor-not-allowed rounded-lg bg-blue-500 w-[48%] py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
              >
                Save
              </button>
            </div>
          </div>
        </Form>
      )}

      <CustomModal
        isVisible={openUpdateFatca}
        centered
        width={450}
        onClose={() => {
          setOpenUpdateFatca(false);
        }}
        className="delete-modal"
      >
        <div className="modal-container flex flex-col items-center justify-center">
          <img
            src="/images/fatca-update.svg"
            alt="kamco update fatca"
            className="mb-3"
          />
          <p className=" text-center">
            You need to update FATCA information because you have an american
            nationality!
          </p>
          <div className="buttons-container flex items-center justify-between">
            <button
              className="cancel flex items-center justify-center"
              onClick={() =>
                navigate(
                  `/home/business-account/${accountId}/${activeKyc}/details?tab=1${isApproveCase ? "&approve=1" : ""
                  }`
                )
              }
            >
              Cancel
            </button>

            <button
              className="flex items-center justify-center"
              onClick={() => navigate(`/crs/${accountId}/${activeKyc}/edit`)}
            >
              Update
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default EditAccountDetails;
